import { useRef, useState } from 'react';
import type { FotoData } from '@shared/types';

interface Props {
  state: FotoData;
  onChange: (data: FotoData) => void;
  onNext: () => void;
  onBack: () => void;
  onUpload: (file: File) => Promise<void>;
  isUploading: boolean;
  uploadError: string | null;
  returnToSamenvatting?: boolean;
  onReturnToSamenvatting?: () => void;
}

export default function StapFoto({
  state,
  onChange,
  onNext,
  onBack,
  onUpload,
  isUploading,
  uploadError,
  returnToSamenvatting,
  onReturnToSamenvatting,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [foutMelding, setFoutMelding] = useState<string | null>(null);

  function handleFile(file: File) {
    setFoutMelding(null);
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
    if (!allowed.includes(file.type) && !file.name.toLowerCase().endsWith('.heic')) {
      setFoutMelding('Alleen JPG, PNG, WebP of HEIC bestanden zijn toegestaan.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setFoutMelding('Bestand te groot. Maximaal 10 MB toegestaan.');
      return;
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    onUpload(file);
  }

  function handleChange(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    if (file) handleFile(file);
  }

  function handleDrop(ev: React.DragEvent) {
    ev.preventDefault();
    const file = ev.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function verwijder() {
    setPreviewUrl(null);
    onChange({ ...state, uploadId: null, aiAnalyse: null });
    if (inputRef.current) inputRef.current.value = '';
  }

  function goHandmatig() {
    onChange({ ...state, gebruikHandmatig: true });
  }

  const kanDoorgaan = state.uploadId !== null || state.gebruikHandmatig;

  return (
    <div className="lc-stap lc-stap--foto">
      <h2 className="lc-stap__titel">Foto van je meterkast</h2>
      <p className="lc-stap__beschrijving">
        Onze AI analyseert je meterkast automatisch. Zo hoef je minder handmatig in te vullen.
      </p>

      {/* Tips */}
      <div className="lc-info-blok">
        <p className="lc-info-blok__titel">Zo maak je een goede foto:</p>
        <ul className="lc-info-blok__lijst">
          <li>Open de deur van je meterkast volledig</li>
          <li>Zorg voor voldoende licht (zaklamp mag)</li>
          <li>Fotografeer de volledige binnenkant</li>
          <li>Houd de camera recht, niet schuin</li>
        </ul>
      </div>

      {/* Upload area */}
      {!previewUrl && (
        <div
          className="lc-upload"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
          aria-label="Klik of sleep een foto hier naartoe"
        >
          <span className="lc-upload__icoon" aria-hidden="true">📷</span>
          <p className="lc-upload__tekst">Klik om foto te kiezen of sleep hier naartoe</p>
          <p className="lc-upload__hint">JPG, PNG, WebP, HEIC · max 10 MB</p>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,.heic"
            className="lc-upload__input"
            onChange={handleChange}
            aria-hidden="true"
            tabIndex={-1}
          />
        </div>
      )}

      {/* Preview */}
      {previewUrl && (
        <div className="lc-upload__preview">
          <img src={previewUrl} alt="Foto van meterkast" className="lc-upload__img" />
          <button
            type="button"
            className="lc-knop lc-knop--klein lc-knop--gevaar"
            onClick={verwijder}
            disabled={isUploading}
          >
            Verwijder foto
          </button>
        </div>
      )}

      {/* Upload status */}
      {isUploading && (
        <p className="lc-status lc-status--laden" role="status" aria-live="polite">
          <span className="lc-spinner" aria-hidden="true" /> Foto wordt geanalyseerd…
        </p>
      )}

      {state.aiAnalyse && !isUploading && (
        <p className="lc-status lc-status--succes" role="status" aria-live="polite">
          ✅ Analyse klaar — we tonen de resultaten bij de samenvatting.
        </p>
      )}

      {(uploadError || foutMelding) && (
        <p className="lc-status lc-status--fout" role="alert">
          {uploadError ?? foutMelding}
        </p>
      )}

      <div className="lc-stap__acties">
        {returnToSamenvatting && (
          <button type="button" className="lc-knop lc-knop--terug-samenvatting" onClick={onReturnToSamenvatting}>
            ← Terug naar samenvatting
          </button>
        )}
        <button type="button" className="lc-knop lc-knop--terug" onClick={onBack}>
          ← Terug
        </button>

        {kanDoorgaan && (
          <button
            type="button"
            className="lc-knop lc-knop--primair"
            onClick={onNext}
            disabled={isUploading}
          >
            {returnToSamenvatting ? 'Opslaan & terug →' : 'Volgende →'}
          </button>
        )}
      </div>

      <button
        type="button"
        className="lc-link lc-stap__fallback"
        onClick={goHandmatig}
      >
        Ik kan geen foto maken → vul handmatig in
      </button>
    </div>
  );
}
